import { Modal } from '@/components/modal/modal';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useEmailConnectCallback } from '@/hooks/use-email-connect-callback';
import { isValidEmail } from '@/lib/is-valid-email';
import { Button, HStack, Input, Link, Text } from '@chakra-ui/react';
import type { EmailMetadata } from '@fluenceur/types';
import type { UseMutationResult } from '@tanstack/react-query';
import { RefreshDouble } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IntegrationCard from './IntegrationCard';
import type { IntegrationCardVariant } from './IntegrationCard';

/**
 * Gmail's "Accounts and Import" settings tab, where "Send mail as" aliases are
 * declared and verified. `authuser` selects the connected account when the
 * browser is signed into several, instead of defaulting to the first one.
 */
const gmailSendAsSettingsUrl = (accountEmail: string) =>
  `https://mail.google.com/mail/?authuser=${encodeURIComponent(accountEmail)}#settings/accounts`;

export interface EmailIntegrationConfig {
  i18nPrefix: string;
  /** URL param key to read from the callback redirect (e.g., 'gmail' or 'outlook') */
  callbackParam: string;
  icon: string;
}

interface EmailIntegrationProps {
  id: string | null;
  isConnected: boolean;
  needsRefresh?: boolean;
  senderEmail?: string;
  metadata?: EmailMetadata;
  config: EmailIntegrationConfig;
  redirectMutation: UseMutationResult<{ redirectUrl: string }, Error, void>;
  disconnectMutation: UseMutationResult<void, Error, void>;
  updateSenderMutation?: UseMutationResult<
    { senderEmail: string; metadata: EmailMetadata },
    Error,
    string
  >;
  variant?: IntegrationCardVariant;
  detailHref?: string;
  disclosure?: ReactNode;
}

const EmailIntegration = ({
  isConnected,
  needsRefresh = false,
  senderEmail,
  metadata,
  config,
  redirectMutation,
  disconnectMutation,
  updateSenderMutation,
  variant,
  detailHref,
  disclosure,
}: EmailIntegrationProps) => {
  const { t } = useTranslation();
  const [senderModalOpen, setSenderModalOpen] = useState(false);

  const isLoading =
    redirectMutation.isPending || disconnectMutation.isPending || !!updateSenderMutation?.isPending;
  const currentSenderEmail = senderEmail ?? metadata?.email ?? '';
  const [senderDraft, setSenderDraft] = useState(currentSenderEmail);
  const canManageSender = isConnected && !!updateSenderMutation;
  const trimmedSenderDraft = senderDraft.trim();
  // Format only, the same rule as every other email field of the app; Gmail has the last word.
  const canSaveSender =
    trimmedSenderDraft !== currentSenderEmail && isValidEmail(trimmedSenderDraft);

  const redirectToProvider = async () => {
    try {
      const { redirectUrl } = await redirectMutation.mutateAsync();
      toaster.create({
        description: t(`${config.i18nPrefix}.redirecting`),
        type: 'loading',
      });
      window.location.href = redirectUrl;
    } catch {
      toaster.create({
        description: t(`${config.i18nPrefix}.errors.connectionFailed`),
        type: 'error',
      });
    }
  };

  // Handle backend redirect result (backend exchanged the code and redirects here)
  useEmailConnectCallback({
    callbackParam: config.callbackParam,
    i18nPrefix: config.i18nPrefix,
  });

  const handleToggle = async () => {
    if (isConnected) {
      try {
        await disconnectMutation.mutateAsync();
        toaster.create({
          description: t(`${config.i18nPrefix}.success.disconnected`),
          type: 'success',
        });
      } catch {
        toaster.create({
          description: t(`${config.i18nPrefix}.errors.disconnectFailed`),
          type: 'error',
        });
      }
    } else {
      await redirectToProvider();
    }
  };

  const openSenderModal = () => {
    setSenderDraft(currentSenderEmail);
    setSenderModalOpen(true);
  };

  const handleSenderSave = async () => {
    if (!updateSenderMutation || !canSaveSender) return;

    try {
      await updateSenderMutation.mutateAsync(trimmedSenderDraft);
      toaster.create({
        description: t(`${config.i18nPrefix}.success.senderUpdated`),
        type: 'success',
      });
      setSenderModalOpen(false);
    } catch {
      // The modal stays open with the typed value so the brand can correct it.
      toaster.create({
        description: t(`${config.i18nPrefix}.errors.senderUpdateFailed`),
        type: 'error',
      });
    }
  };

  const reconnectAction = needsRefresh ? (
    <Button
      size="sm"
      colorPalette="orange"
      onClick={redirectToProvider}
      loading={redirectMutation.isPending}
      disabled={isLoading}
    >
      <RefreshDouble width={16} height={16} />
      <Text as="span" ml={1}>
        {t('integrations.card.reconnect')}
      </Text>
    </Button>
  ) : undefined;

  const senderAction =
    !needsRefresh && canManageSender ? (
      <Button size="sm" variant="ghost" colorPalette="gray" onClick={openSenderModal}>
        {t(`${config.i18nPrefix}.sender.manageCta`)}
      </Button>
    ) : undefined;

  const senderModal = canManageSender ? (
    <Modal
      isOpen={senderModalOpen}
      onClose={() => setSenderModalOpen(false)}
      title={t(`${config.i18nPrefix}.sender.manageTitle`)}
      description={t(`${config.i18nPrefix}.sender.manageDescription`)}
      maxWidth="md"
      isCentered
      footer={
        <HStack justify="flex-end" width="full" gap={2}>
          <Button
            size="sm"
            variant="outline"
            colorPalette="gray"
            onClick={() => setSenderModalOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            colorPalette="gray"
            onClick={handleSenderSave}
            loading={updateSenderMutation?.isPending}
            disabled={!canSaveSender}
          >
            {t('common.save')}
          </Button>
        </HStack>
      }
    >
      <Field label={t(`${config.i18nPrefix}.sender.label`)}>
        <Input
          variant="outline"
          type="email"
          size="sm"
          autoComplete="off"
          value={senderDraft}
          placeholder={t(`${config.i18nPrefix}.sender.placeholder`)}
          onChange={(event) => setSenderDraft(event.target.value)}
          disabled={isLoading}
        />
      </Field>
      {metadata?.email && (
        <Link
          href={gmailSendAsSettingsUrl(metadata.email)}
          target="_blank"
          rel="noopener noreferrer"
          variant="underline"
          fontSize="sm"
          color="blue.600"
          mt={2}
        >
          {t(`${config.i18nPrefix}.sender.gmailSettingsLink`)}
        </Link>
      )}
    </Modal>
  ) : undefined;

  return (
    <>
      <IntegrationCard
        name={t(`${config.i18nPrefix}.name`)}
        description={t(`${config.i18nPrefix}.description`)}
        icon={config.icon}
        iconStyle="mark"
        isActive={isConnected}
        isLoading={isLoading}
        disabled={isLoading}
        onToggle={handleToggle}
        connectedAs={metadata?.email}
        connectedLabel={
          needsRefresh ? t('integrations.reconnectRequiredLabel') : t('integrations.connectedLabel')
        }
        connectedLabelColorPalette={needsRefresh ? 'orange' : 'green'}
        variant={variant}
        detailHref={detailHref}
        inlineAction={reconnectAction}
        detailAction={senderAction}
        disclosure={disclosure}
      />
      {senderModal}
    </>
  );
};

export default EmailIntegration;
